import React, { useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { formatDuration } from '../../../utils/formatDuration';
import { formatTrendTick, formatTrendRange, formatRate } from '../summaryFormatters';
import { VIOLATION_VOCAB } from '../summaryVocab';

const buildMetrics = (vocab) => [
  { id: 'count', label: 'Количество', key: vocab.countKey, format: (v) => String(v ?? '—') },
  { id: 'duration', label: 'Длительность', key: vocab.durationKey, format: formatDuration },
  { id: 'rate', label: 'На 100 часов', key: vocab.rateKey, format: formatRate },
];

const CURRENT_COLOR = '#2563eb';
const PREVIOUS_COLOR = '#94a3b8';

/**
 * ТЗ V5 №3: предыдущий период накладывается по порядку корзин. Backend отдаёт
 * previous_points той же длины и того же bucket, что points, — i-я корзина
 * прошлого периода стоит под i-й текущей. Лишние или недостающие точки не
 * выдумываются: нет пары — нет значения (разрыв линии, не 0).
 */
export const mergeTrendWithPrevious = (points, previousPoints, key) => {
  const previous = Array.isArray(previousPoints) ? previousPoints : [];
  return (Array.isArray(points) ? points : []).map((point, index) => {
    const pair = previous[index];
    return {
      ...point,
      value: point[key],
      previous: pair ? (pair[key] ?? null) : null,
      previousFrom: pair?.from,
      previousTo: pair?.to,
    };
  });
};

/**
 * ТЗ №1 §9.1: один trend.points, три режима отображения; buckets не
 * пересобираются; null rate — разрыв линии, не 0; tooltip — exact from–to. Линия
 * ломаная: сглаживание рисовало между точками значения, которых нет.
 * Подписи — по scope.trend_timezone (дополнение §2.1), не по браузеру.
 */
const TrendChart = ({ trend, trendTimezone, vocab = VIOLATION_VOCAB }) => {
  const [metricId, setMetricId] = useState('count');
  const [showPrevious, setShowPrevious] = useState(true);
  const metrics = buildMetrics(vocab);
  const metric = metrics.find((m) => m.id === metricId);
  const tz = trendTimezone || 'UTC';
  const points = mergeTrendWithPrevious(trend?.points, trend?.previous_points, metric.key);
  const hasPrevious = points.some((point) => point.previousFrom);
  const previousVisible = hasPrevious && showPrevious;

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    return (
      <div className="sum-tooltip">
        <div className="sum-tooltip-range">{formatTrendRange(point.from, point.to, tz)}</div>
        <div><b>{metric.format(point.value)}</b></div>
        {previousVisible && point.previousFrom && (
          <div className="sum-tooltip-previous">
            <div className="sum-tooltip-range">{formatTrendRange(point.previousFrom, point.previousTo, tz)}</div>
            <div>{metric.format(point.previous)}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="sum-card">
      <div className="sum-card-head">
        <h2 className="sum-card-title">{vocab.trendTitle}</h2>
        <span className="sum-tz" title="Часовой пояс графика">{tz}</span>
        <div className="sum-segment" role="group" aria-label="Метрика">
          {metrics.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`sum-preset${metricId === item.id ? ' sum-preset--active' : ''}`}
              onClick={() => setMetricId(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      {points.length === 0 ? <p className="sum-muted">Нет данных за период</p> : (
        <>
          <div className="sum-trend-legend">
            <span className="sum-trend-key">
              <span className="sum-trend-swatch" style={{ background: CURRENT_COLOR }} aria-hidden="true" />
              Выбранный период
            </span>
            {hasPrevious ? (
              <label className="sum-trend-key">
                <input
                  type="checkbox"
                  checked={showPrevious}
                  onChange={(event) => setShowPrevious(event.target.checked)}
                />
                <span className="sum-trend-swatch sum-trend-swatch--dashed" style={{ borderColor: PREVIOUS_COLOR }} aria-hidden="true" />
                Предыдущий период
              </label>
            ) : (
              <span className="sum-muted">Данных за предыдущий период нет</span>
            )}
          </div>
          <div className="sum-chart">
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={points} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="from"
                  tickFormatter={(iso) => formatTrendTick(iso, trend.bucket, tz)}
                  minTickGap={24}
                  fontSize={12}
                />
                <YAxis
                  allowDecimals={metricId === 'rate'}
                  fontSize={12}
                  tickFormatter={metricId === 'duration' ? (v) => `${Math.round(v / 60)} мин` : undefined}
                />
                <Tooltip content={renderTooltip} />
                <Area
                  type="linear"
                  dataKey="value"
                  name="Выбранный период"
                  stroke={CURRENT_COLOR}
                  fill={CURRENT_COLOR}
                  fillOpacity={0.15}
                  connectNulls={false}
                />
                {previousVisible && (
                  <Line
                    type="linear"
                    dataKey="previous"
                    name="Предыдущий период"
                    stroke={PREVIOUS_COLOR}
                    strokeWidth={2}
                    strokeDasharray="5 4"
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </section>
  );
};

export default TrendChart;
